import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { parseUnitObjective } from "@examspark/api";
import { exists, filterMaybeArray } from "@examspark/common";
import {
  PrintPerformanceAssessmentModal,
  ScrollToTop,
  useModal,
  useQueriesGetResources,
  useQueryGetPerformanceTask,
  useQueryGetUnit,
  useQuerySearchPerformanceTaskUsages,
  useAppContext,
} from "@examspark/common-app";
import { Modal, Text, VSpace } from "@examspark/common-ui";
import { IconDocumentLine } from "@examspark/common-ui/dist/components/icons";

import BrowseList from "../../components/browse/BrowseList";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import ContentSection from "../../components/common/ContentSection";
import { Feedback } from "../../components/common/Feedback";
import Objectives from "../../components/common/Objectives";
import PageHead from "../../components/common/PageHead";
import PageMenu from "../../components/common/PageMenu";
import PageTitle from "../../components/common/PageTitle";
import UnitContent from "../../components/common/UnitContent";
import ResourcesList from "../../components/resources/ResourcesList";
import ResourceTools from "../../components/resources/ResourceTools";
import Rubric from "../../components/unit/Rubric";
import { useFlags } from "../../hooks/useFlags";
import { useUsername } from "../../hooks/useUsername";

const AssessmentRoute = () => {
  const params = useParams<{
    id: string;
  }>();
  const [unitId, setUnitId] = useState<string>();
  const [state] = useAppContext();
  const { data } = useQueryGetPerformanceTask(params.id, "resource#v0");
  const { data: usages } = useQuerySearchPerformanceTaskUsages(params.id);
  const { data: unit } = useQueryGetUnit(unitId);
  const resourcesQueries = useQueriesGetResources(
    filterMaybeArray(data?.resourcesIds),
  );

  const resources = useMemo(
    () => resourcesQueries.map((q) => q.data).filter(exists),
    [resourcesQueries],
  );

  const rubricData = data?.rubrics?.[0].rubric;
  const rubricContent = rubricData?.content?.rubric as string;

  useEffect(() => {
    if (usages?.items?.length === 1) {
      setUnitId(usages.items[0].id);
    }
  }, [usages]);

  const paObjectives = useMemo(
    () =>
      data?.objectives
        ?.map(
          (paObjective) =>
            unit?.objectives?.find(
              (unitObjective) => unitObjective.id === paObjective.id,
            ) ?? paObjective,
        )
        .map((o) => parseUnitObjective(o)),
    [data?.objectives, unit?.objectives],
  );

  const isObjectiveAlignedToUnit = (objectiveId: string) =>
    unit?.objectives?.find((o) => o.id === objectiveId) != null;

  const flags = useFlags();
  const printPerformanceAssessmentModal = useModal();
  const username = useUsername();

  if (!params.id) return null;

  const showSelectModal =
    unitId == null && !!usages?.items?.length && usages.items.length > 1;

  return (
    <>
      {state.workspace && (
        <PrintPerformanceAssessmentModal
          title="Download PA as PDF"
          performanceAssessmentId={params.id}
          username={username}
          showStandards={false}
          workspace={state.workspace?.name ?? null}
          workspaceId={state.workspace?.id}
          {...printPerformanceAssessmentModal.modalProps}
        />
      )}
      <ScrollToTop />
      <Modal isOpen={showSelectModal} title="Select Unit" size="medium">
        <Modal.PaddedBody>
          <BrowseList
            data={usages?.items ?? []}
            onResultSelect={(item) => setUnitId(item.id)}
          />
        </Modal.PaddedBody>
      </Modal>
      <VSpace size="3rem">
        <PageHead>
          <Breadcrumbs items={Breadcrumbs.Unit(unit)} />
          <PageTitle
            tools={
              <PageMenu
                buttons={[
                  flags.printPerformanceAssessmentEnabled && {
                    title: "Download as PDF",
                    icon: <IconDocumentLine />,
                    onClick: () => printPerformanceAssessmentModal.open(),
                  },
                ]}
              />
            }
          >
            {data?.name}
          </PageTitle>
        </PageHead>

        {!!data?.content?.body && (
          <VSpace
          // style={{
          //   padding: "1rem",
          //   border: "solid 1px #ddd",
          //   borderRadius: "1rem",
          // }}
          >
            <Text.H2>
              Performance Assessment {<ResourceTools resource={data} />}
            </Text.H2>
            <UnitContent content={data.content?.body} />
          </VSpace>
        )}
        {rubricContent && (
          <VSpace size="2rem">
            <Text.H2>
              Scoring Rubric {rubricData && <Feedback resource={rubricData} />}
            </Text.H2>
            <Rubric data={JSON.parse(rubricContent)} />
          </VSpace>
        )}
        {!!resources.length && (
          <ContentSection title="Resources">
            <ResourcesList resources={resources} />
          </ContentSection>
        )}
        {!!paObjectives?.length && (
          <ContentSection title="Assessed TEKS">
            <Objectives
              objectives={paObjectives}
              unit={unit}
              renderObjectiveDetails={(o, isInjected) =>
                isObjectiveAlignedToUnit(o.id) === false &&
                !isInjected && <i>Not aligned to unit</i>
              }
            />
          </ContentSection>
        )}
      </VSpace>
    </>
  );
};

export default AssessmentRoute;
